/**
 * One property: its details, units, ownerships and files.
 *
 * The ownership panel is the one worth reading carefully. Those rows are what scope
 * an owner's access to THIS property's live records — `app_owner_can_see_property()`
 * matches an ownership period that covers today, so ending one cuts off units,
 * leases, and current reports for it from the day after the end date. There is no
 * delete, only end. The owner's portal login itself is untouched, and so is anything
 * already shared with them: `documents_owner_select` checks ownership as of each
 * document's own upload date, not today, so a statement or notice they already had
 * access to stays retrievable. The UI describes that distinction rather than the
 * older, broader "revokes their portal access" wording, which overstated it.
 *
 * "Correct owner" is a second, narrower action next to "End ownership" — for
 * the wrong owner having been picked when the row was created, not a real
 * transfer. It reassigns ownerId on the same row in place rather than
 * ending one and granting another, which matters because ending a row
 * defaults its end date to today and access doesn't free up until the day
 * after — a real transfer legitimately hits that same-day 0%-available
 * wall, but a pure data-entry mistake has no reason to. The API
 * (correctOwnershipOwner, properties/service.ts) refuses once any statement
 * has already accounted for the current owner on this property; this UI
 * does not duplicate that check, it just surfaces the API's own message.
 *
 * A same-labeled "Correct owner" button also appears when the property
 * currently has NO owner at all — attached to the most recently ended row,
 * since that's the one an accidental "End ownership" click most likely
 * produced. Its picker defaults to that row's own owner (so Confirm alone
 * just undoes the mistake) but allows picking someone else too, and it
 * calls a different endpoint (reopenOwnership) that also clears the row's
 * endDate — `correctionMode` is what the two share a picker but not a
 * request path.
 */

import { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useInvalidate, useList, useOne } from '@refinedev/core';
import { api } from '../../../lib/api.js';
import { CoverImage } from '../../../components/CoverImage.js';
import { DataTable, type Column } from '../../../components/DataTable.js';
import { DocumentList } from '../../../components/DocumentList.js';
import { PhotoUpload } from '../../../components/PhotoUpload.js';
import { PropertyPhotoGallery } from '../../../components/PropertyPhotoGallery.js';
import { humanizeEnum } from '../../../components/Form.js';
import { useCan } from '../../../lib/capabilities.js';

interface PropertyRecord {
  id: string;
  name: string;
  propertyType: string;
  address: {
    line1: string;
    line2: string | null;
    city: string;
    state: string;
    postalCode: string;
    county: string | null;
  };
  yearBuilt: number | null;
  parcelNumber: string | null;
  notes: string | null;
  unitCount: number;
  photoUrl: string | null;
  archivedAt: string | null;
}

interface UnitRow {
  id: string;
  label: string;
  bedrooms: number;
  bathrooms: string;
  squareFeet: number | null;
  status: string;
  marketRent: string;
  /** The unit's current listing (step 11 prep), or null if not published. */
  listing: { id: string; publishedAt: string | null } | null;
  archivedAt: string | null;
}

interface OwnershipRow {
  id: string;
  ownerId: string;
  ownerName?: string;
  percent: string;
  startDate: string | null;
  endDate: string | null;
  isCurrent: boolean;
}

interface OwnerOption {
  id: string;
  displayName: string;
  email: string;
}

export function PropertyShow() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const invalidate = useInvalidate();
  const [error, setError] = useState<string | null>(null);

  // Which row's owner picker is open, if any, which owner is picked, and
  // which of the two things it's about to do — inline rather than a
  // prompt() since, unlike an end date, an owner is a choice from a list,
  // not a value someone types. 'correct' reassigns a current row's owner in
  // place (correctOwnershipOwner); 'reopen' restores an already-ended row
  // when the property currently has no owner at all (reopenOwnership) — see
  // the render logic below for when each applies.
  const [correctingRowId, setCorrectingRowId] = useState<string | null>(null);
  const [correctionMode, setCorrectionMode] = useState<'correct' | 'reopen'>('correct');
  const [correctionOwnerId, setCorrectionOwnerId] = useState('');
  const [correctionBusy, setCorrectionBusy] = useState(false);

  // Two separate capabilities, because they are two separate risks. Reshaping the
  // portfolio (`inventory:write`) is ADMIN and PROPERTY_MANAGER; attaching a photo
  // (`documents:write`) is every staff role including MAINTENANCE, whose job produces
  // exactly that.
  const canEditInventory = useCan('inventory:write');
  const canWriteDocuments = useCan('documents:write');

  const { data: propertyData, isLoading } = useOne<PropertyRecord>({
    resource: 'properties',
    id: id ?? '',
    queryOptions: { enabled: Boolean(id) },
  });

  const { data: unitsData, isLoading: unitsLoading } = useList<UnitRow>({
    resource: 'units',
    filters: [
      { field: 'propertyId', operator: 'eq', value: id },
      { field: 'includeArchived', operator: 'eq', value: 'true' },
    ],
    pagination: { mode: 'off' },
    queryOptions: { enabled: Boolean(id) },
  });

  const { data: ownershipsData, isLoading: ownershipsLoading } = useList<OwnershipRow>({
    resource: 'ownerships',
    filters: [{ field: 'propertyId', operator: 'eq', value: id }],
    pagination: { mode: 'off' },
    queryOptions: { enabled: Boolean(id) },
  });

  // Only fetched for the "Correct owner" picker below, so it is not gated on
  // canEditInventory here — the picker itself already is, and this list is
  // cheap and staff-readable regardless.
  const { data: ownersData } = useList<OwnerOption>({
    resource: 'owners',
    pagination: { current: 1, pageSize: 200 },
  });
  const owners = ownersData?.data ?? [];

  // No row with endDate === null means nobody currently sees this property
  // in the owner portal — including "ended by mistake." If there's at least
  // one past row, the most recently ended one (by endDate) is where the
  // "Correct owner" reopen action goes; with zero rows ever, there is
  // nothing to reopen and "Add owner" (always visible above the table) is
  // the only path, same as before this change.
  const ownershipRows = ownershipsData?.data ?? [];
  const hasCurrentOwner = ownershipRows.some((row) => row.endDate === null);
  const mostRecentlyEndedRowId = hasCurrentOwner
    ? null
    : ([...ownershipRows].sort((a, b) => (b.endDate ?? '').localeCompare(a.endDate ?? ''))[0]?.id ??
      null);

  const property = propertyData?.data;

  if (isLoading) return <p className="muted">Loading…</p>;
  if (!property) return <p className="alert alert--error">Property not found.</p>;

  const unitColumns: Column<UnitRow>[] = [
    {
      key: 'label',
      header: 'Unit',
      render: (row) => (
        <>
          <strong>{row.label}</strong>
          {row.archivedAt ? (
            <>
              {' '}
              <span className="pill">Archived</span>
            </>
          ) : null}
        </>
      ),
    },
    {
      key: 'beds',
      header: 'Beds / baths',
      render: (row) => `${String(row.bedrooms)} / ${row.bathrooms}`,
    },
    {
      key: 'sqft',
      header: 'Sq ft',
      numeric: true,
      render: (row) => (row.squareFeet === null ? '—' : row.squareFeet.toLocaleString()),
    },
    { key: 'status', header: 'Status', render: (row) => humanizeEnum(row.status) },
    // Money arrives as a string and is rendered as one. Never parsed to a number
    // for display — a float round-trip is how "1250.10" becomes "1250.0999999".
    { key: 'rent', header: 'Market rent', numeric: true, render: (row) => `$${row.marketRent}` },
    {
      key: 'listing',
      header: 'Listed',
      // The row itself navigates to the unit's own page (see the DataTable
      // below) — this cell's button must not also trigger that.
      render: (row) => (
        <div className="row" onClick={(event) => event.stopPropagation()}>
          {row.listing ? (
            <span className="pill">Published</span>
          ) : (
            <span className="muted">Not listed</span>
          )}
          {canEditInventory ? (
            <button
              type="button"
              className="button button--quiet"
              onClick={() => void toggleListing(row)}
            >
              {row.listing ? 'Unpublish' : 'Publish'}
            </button>
          ) : null}
        </div>
      ),
    },
  ];

  /**
   * The public-listing toggle (step 11 prep — see CLAUDE.md's Public
   * listings site section). There is no public page reading this yet; this
   * is the staff-side control ahead of one existing, same as the rest of
   * this app's "build the record before the consumer" moves.
   */
  async function toggleListing(row: UnitRow) {
    setError(null);
    try {
      if (row.listing) {
        await api(`/listings/${row.listing.id}/unpublish`, { method: 'PATCH' });
      } else {
        await api('/listings', { method: 'POST', body: JSON.stringify({ unitId: row.id }) });
      }
      await invalidate({ resource: 'units', invalidates: ['list'] });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not update the listing.');
    }
  }

  async function endOwnership(row: OwnershipRow) {
    const today = new Date().toISOString().slice(0, 10);
    const answer = window.prompt(
      `End ${row.ownerName ?? 'this owner'}'s interest in ${property?.name ?? 'this property'}?\n\n` +
        'From the day after the end date, they will stop seeing this property, its units, ' +
        'and its current reports in the portal. They can still log in, and anything already ' +
        'shared with them — documents, statements, disbursements — stays available.\n' +
        'The row is kept — it justifies statements already issued.\n\n' +
        'End date (YYYY-MM-DD):',
      today,
    );
    if (!answer) return;

    setError(null);
    try {
      await api(`/ownerships/${row.id}/end`, {
        method: 'PATCH',
        body: JSON.stringify({ endDate: answer }),
      });
      await invalidate({ resource: 'ownerships', invalidates: ['list'] });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not end that ownership.');
    }
  }

  /**
   * Fix a wrong-owner data-entry mistake in place, or restore an
   * accidentally-ended row — the two endpoints correctionMode picks
   * between. See correctOwnershipOwner's and reopenOwnership's own doc
   * comments (properties/service.ts) for what each refuses; this function
   * just wires the picker to whichever one applies.
   */
  async function confirmCorrection(row: OwnershipRow) {
    if (!correctionOwnerId) return;
    setError(null);
    setCorrectionBusy(true);
    try {
      const path = correctionMode === 'reopen' ? 'reopen' : 'correct-owner';
      await api(`/ownerships/${row.id}/${path}`, {
        method: 'PATCH',
        body: JSON.stringify({ ownerId: correctionOwnerId }),
      });
      await invalidate({ resource: 'ownerships', invalidates: ['list'] });
      setCorrectingRowId(null);
      setCorrectionOwnerId('');
    } catch (caught) {
      setError(
        caught instanceof Error
          ? caught.message
          : correctionMode === 'reopen'
            ? 'Could not reopen that ownership.'
            : 'Could not correct that ownership.',
      );
    } finally {
      setCorrectionBusy(false);
    }
  }

  const ownershipColumns: Column<OwnershipRow>[] = [
    { key: 'owner', header: 'Owner', render: (row) => row.ownerName ?? row.ownerId },
    { key: 'percent', header: 'Share', numeric: true, render: (row) => `${row.percent}%` },
    { key: 'start', header: 'From', render: (row) => row.startDate ?? '—' },
    {
      key: 'end',
      header: 'To',
      render: (row) => row.endDate ?? <span className="pill">Current</span>,
    },
    {
      key: 'actions',
      header: '',
      render: (row) => {
        if (!canEditInventory) return null;

        // Deliberately row.endDate === null, not row.isCurrent. isCurrent
        // means "access is still active today," which stays true through the
        // end date itself (access cuts off the day after — see the dialog's
        // own copy and app_owner_can_see_property). The end-ownership prompt
        // defaults to today's date, so accepting that default used to leave
        // this button visible right after successfully ending the ownership,
        // with nothing on screen making clear the write had landed. Gating
        // on endDate instead means the button disappears the moment an end
        // date exists, regardless of whether it's taken effect yet.
        const isOpenRow = row.endDate === null;
        // A property with no current owner can't tell "I ended the wrong
        // row" from "the owner on this row was already wrong" from the
        // outside — reopenOwnership covers both, so this is the one ended
        // row that gets a picker: the most recent one, and only while
        // nothing else is currently open for this property.
        const isReopenCandidate =
          !isOpenRow && !hasCurrentOwner && row.id === mostRecentlyEndedRowId;
        if (!isOpenRow && !isReopenCandidate) return null;

        if (correctingRowId === row.id) {
          const pickerOwners =
            correctionMode === 'reopen'
              ? owners
              : owners.filter((owner) => owner.id !== row.ownerId);
          return (
            <div className="row">
              <select
                className="field__input"
                value={correctionOwnerId}
                onChange={(event) => setCorrectionOwnerId(event.target.value)}
                disabled={correctionBusy}
              >
                <option value="">
                  {correctionMode === 'reopen' ? 'Restore to…' : 'Correct owner…'}
                </option>
                {pickerOwners.map((owner) => (
                  <option key={owner.id} value={owner.id}>
                    {owner.displayName}
                  </option>
                ))}
              </select>
              <button
                type="button"
                className="button button--quiet"
                disabled={!correctionOwnerId || correctionBusy}
                onClick={() => void confirmCorrection(row)}
              >
                {correctionBusy ? 'Saving…' : 'Confirm'}
              </button>
              <button
                type="button"
                className="button button--quiet"
                disabled={correctionBusy}
                onClick={() => {
                  setCorrectingRowId(null);
                  setCorrectionOwnerId('');
                }}
              >
                Cancel
              </button>
            </div>
          );
        }

        if (isOpenRow) {
          return (
            <div className="row">
              <button
                type="button"
                className="button button--quiet"
                onClick={() => void endOwnership(row)}
              >
                End ownership
              </button>
              <button
                type="button"
                className="button button--quiet"
                title="Only for a wrong owner picked by mistake — not a real transfer. Refused once a statement has already accounted for this owner on this property."
                onClick={() => {
                  setCorrectingRowId(row.id);
                  setCorrectionMode('correct');
                  setCorrectionOwnerId('');
                }}
              >
                Correct owner
              </button>
            </div>
          );
        }

        // isReopenCandidate: no current owner on this property, and this is
        // its most recently ended row. Prefilled with the row's own owner
        // so hitting Confirm right away just restores things as they were;
        // picking someone else fixes the owner in the same step.
        return (
          <button
            type="button"
            className="button button--quiet"
            title="This property currently has no owner. Restores this row — same owner, or pick a different one if it was also wrong."
            onClick={() => {
              setCorrectingRowId(row.id);
              setCorrectionMode('reopen');
              setCorrectionOwnerId(row.ownerId);
            }}
          >
            Correct owner
          </button>
        );
      },
    },
  ];

  return (
    <section className="stack">
      <div className="row">
        <h1>{property.name}</h1>
        {property.archivedAt ? <span className="pill">Archived</span> : null}
        <span className="shell__spacer" />
        <button
          type="button"
          className="button button--quiet"
          onClick={() => void navigate('/staff/properties')}
        >
          ← All properties
        </button>
        {canEditInventory ? (
          <button
            type="button"
            className="button"
            onClick={() => void navigate(`/staff/properties/${property.id}/edit`)}
          >
            Edit
          </button>
        ) : null}
      </div>

      {error ? <p className="alert alert--error">{error}</p> : null}

      <CoverImage src={property.photoUrl} alt={property.name} variant="hero" />

      <div className="card stack">
        <div className="row">
          <span className="pill">{humanizeEnum(property.propertyType)}</span>
          {property.yearBuilt ? <span className="muted">Built {property.yearBuilt}</span> : null}
          {property.parcelNumber ? (
            <span className="muted">Parcel {property.parcelNumber}</span>
          ) : null}
        </div>
        <address>
          {property.address.line1}
          {property.address.line2 ? <>, {property.address.line2}</> : null}
          <br />
          {property.address.city}, {property.address.state} {property.address.postalCode}
          {property.address.county ? <> · {property.address.county} County</> : null}
        </address>
        {property.notes ? <p>{property.notes}</p> : null}
      </div>

      <div className="row">
        <h2>Units</h2>
        <span className="shell__spacer" />
        {canEditInventory ? (
          <button
            type="button"
            className="button"
            onClick={() => void navigate(`/staff/properties/${property.id}/units/new`)}
          >
            Add unit
          </button>
        ) : null}
      </div>
      <DataTable
        columns={unitColumns}
        rows={unitsData?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={unitsLoading}
        // A unit's own page is a read view — see UnitShow.tsx — so every
        // role that can see this table can click through, same as the
        // Properties list's cards. Editing stays gated, via a button on
        // that page rather than here.
        onRowClick={(row: UnitRow) => void navigate(`/staff/units/${row.id}`)}
        emptyMessage="No units yet. A property needs at least one to hold a lease."
      />

      <div className="row">
        <h2>Ownership</h2>
        <span className="shell__spacer" />
        {canEditInventory ? (
          <button
            type="button"
            className="button"
            onClick={() => void navigate(`/staff/properties/${property.id}/ownerships/new`)}
          >
            Add owner
          </button>
        ) : null}
      </div>
      <p className="muted">
        Share define ownership definitions and payment outputs. Use discernment when defining
        multiple owners to a single property.
      </p>
      <DataTable
        columns={ownershipColumns}
        rows={ownershipsData?.data ?? []}
        rowKey={(row) => row.id}
        isLoading={ownershipsLoading}
        emptyMessage="No owner on record. Nobody can see this property in the owner portal."
      />

      <h2>Photos</h2>
      <PropertyPhotoGallery propertyId={property.id} canWrite={canWriteDocuments} />

      <h2>Files</h2>
      <DocumentList filter={{ propertyId: property.id }} canDelete={canWriteDocuments} />
      {canWriteDocuments ? (
        <details>
          <summary>Upload a photo or document</summary>
          <div className="card">
            <PhotoUpload
              parent={{ propertyId: property.id }}
              defaultDocumentType="PROPERTY_PHOTO"
              onUploaded={() => {
                void invalidate({ resource: 'documents', invalidates: ['list'] });
                // So a newly uploaded PROPERTY_PHOTO shows up in the hero
                // and the Photos gallery above without a manual reload.
                void invalidate({ resource: 'properties', invalidates: ['detail'] });
              }}
            />
          </div>
        </details>
      ) : null}
    </section>
  );
}
